import { FormattedMessage, useIntl } from 'react-intl';
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  FormInstance,
  Checkbox,
  Button,
  Dropdown,
  Menu,
  notification,
  Card,
} from 'antd';

import FloatingFormItem from '@/components/_common/FloatingFormItem';

import {
  Group,
  InputDate,
  InputDecimal,
  InputSearch,
  InputText,
} from '@/components/_common';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import {
  Purchase,
  PurchaseCreateFormValues,
  PurchaseDeliveryType,
  PurchaseOperationDestination,
} from '@/core/domain/purchase/Purchase.types';
import moment from 'moment';
import { UserPlus } from 'react-feather';
import SuppliersSelectedModal from './../../../_common/SuppliersSelectedModal/index';
import { SupplierReduced } from '@/core/domain/people/Suppliers/Suppliers.types';
import { Info } from 'react-feather';
import SituationFormField from '../../DirectSale/SituationFormField';
import { useReleasePurchaseFormContext } from '@/pages/MovementsPage/PurchasesPage/ReleasesPage/ReleasesPurchaseProvider';
import CompaniesInfiniteSelectField from '@/components/_common/CompaniesInfiniteSelectField';
import DocumentTypesInfiniteSelectField from '@/components/_common/DocumentTypesInfiniteSelectField';
import SuppliersCreateModal from '@/components/_common/SuppliersCreateModal';
import { Status } from '@/utils/enum/statusEnum';
import { useSuppliersReducedMutation } from '@/core/domain/people/Suppliers/Suppliers.hooks';
import { useEffect, useMemo } from 'react';
import { useItemsPurchases } from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import PurcheseAlertBoshMigration from '../PurchaseAlertBoshMigration';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { MatrixPartnerOrigin } from '@/types/Register';
import SelectGovernmentOperation from '@/components/_common/TaxReform/SelectGovernmentOperation';
import SelectGovernmentType from '@/components/_common/TaxReform/SelectGovernmentType';

interface Props {
  form: FormInstance<PurchaseCreateFormValues>;
  isEdit?: boolean;
  purchase?: Purchase;
}

function PurchasesReleaseForm({ form, isEdit, purchase }: Props) {
  const intl = useIntl();

  const { situation, purchaseId, itemsPurchase } =
    useReleasePurchaseFormContext();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const [modalSupplierVisible, modalSupplierAction] = useDisclosure();

  const [suppliersCreateVisible, setSuppliersCreateVisible] = useDisclosure();

  const watchDeliveryValue = Form.useWatch('deliveryValue', form);
  const watchExpensesValue = Form.useWatch('expensesValue', form);
  const watchDeliveryType = Form.useWatch('deliveryType', form);

  const isWorkOrderItem = !!itemsPurchase?.find(
    (item) => !!item.workOrderNumber,
  );

  const { data: matrix } = useMatrix();

  const disableIssueDateForBosch = useMemo(
    () =>
      purchase?.importedPurchase &&
      !!purchase.xmlPath &&
      matrix?.partnerOrigin === MatrixPartnerOrigin.BOSCH_CAR_SERVICE,
    [purchase?.importedPurchase, purchase?.xmlPath, matrix?.partnerOrigin],
  );

  const { data } = useItemsPurchases({
    purchaseId: +purchaseId,
    enabled: !!purchaseId,
  });

  const purchaseItems = flatInfiniteData(data);

  const suppliersMutation = useSuppliersReducedMutation();

  useEffect(() => {
    const calculateTotalItem = () => {
      const isRecipient =
        PurchaseDeliveryType.recipient === purchase?.deliveryType;

      const totalAmount = purchase?.totalAmount ?? 0;
      const deliveryValue = isRecipient ? purchase?.deliveryValue ?? 0 : 0;
      const expensesValue = purchase?.expensesValue ?? 0;

      return totalAmount - deliveryValue - expensesValue;
    };

    const calculateTotalAmount = () => {
      const isRecipient = PurchaseDeliveryType.recipient === watchDeliveryType;

      const totalItem = calculateTotalItem();
      const deliveryValue = isRecipient ? watchDeliveryValue ?? 0 : 0;
      const expensesValue = watchExpensesValue ?? 0;

      return totalItem + deliveryValue + expensesValue;
    };

    const totalAmount = calculateTotalAmount();

    form.setFieldsValue({ totalAmount });
  }, [watchDeliveryValue, watchExpensesValue, watchDeliveryType]);

  const handleSupplierSelect = (supplier?: SupplierReduced) => {
    form.setFieldsValue({
      supplier: {
        id: supplier?.id,
        name: supplier?.name,
      },
    });
    modalSupplierAction.close();
  };

  const expensesIndividuallyMenu = (
    <Menu mode="vertical" style={{ width: 300 }}>
      <Menu.Item key={1}>
        {intl.formatMessage({
          id: 'purchases.releases.form.expensesIndividually.info',
        })}
      </Menu.Item>
    </Menu>
  );

  const handleKeyNfe = async (value: string) => {
    if (value.length === 44) {
      const cpfCnpj = value.slice(6, 20);
      const modelNf = value.slice(20, 22);
      const seriesNf = value.slice(22, 25);
      const documentNumber = value.slice(25, 34);

      form.setFieldsValue({
        modelNf,
        seriesNf,
        documentNumber,
      });

      const { data } = await suppliersMutation.mutateAsync({ cpfCnpj });

      if (!data.length) {
        return notification.warning({
          message: `Fornecedor não encontrado para a chave da nota fiscal.`,
        });
      }

      form.setFieldsValue({
        supplier: {
          id: data[0]?.id,
          name: data[0]?.name,
        },
      });
    }
  };

  const purchaseReadOnly = !!purchase?.boschMigration;
  const isProPartsPurchase = !!purchase?.proPartsComplementId;

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        name="releasePurchaseGeneral"
        disabled={purchaseReadOnly}
      >
        <Row gutter={[16, 9]}>
          {!situation && (
            <Col span={24}>
              <SituationFormField
                initialValue={form.getFieldValue('situation')}
              />
            </Col>
          )}

          {purchase?.boschMigration && (
            <Col span={24}>
              <PurcheseAlertBoshMigration />
            </Col>
          )}

          {isEdit && (
            <Col span={24} lg={3} xl={2}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'purchases.releases.form.code',
                })}
                name="code"
              >
                <Input disabled={isEdit} />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24} md={12} lg={isEdit ? 10 : 12} xl={7}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchases.releases.form.company',
              })}
              name="companyId"
              rules={[defaultEmptyFieldError]}
            >
              <CompaniesInfiniteSelectField
                queryFilter={{ active: true }}
                disabled={
                  isWorkOrderItem ||
                  purchase?.situation === Status.authorized ||
                  purchase?.situation === Status.canceled ||
                  isProPartsPurchase
                }
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={isEdit ? 11 : 12} xl={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchases.releases.form.invoiceKey',
              })}
              name="keyNfe"
              rules={[{ min: 44 }]}
            >
              <InputText
                onlyNumber
                maxLength={44}
                disabled={purchase?.situation === Status.canceled}
                onChange={({ target: { value } }) => handleKeyNfe(value)}
              />
            </FloatingFormItem>
          </Col>

          <Col span={22} md={10} lg={10} xl={isEdit ? 8 : 10}>
            <Form.Item hidden name={['supplier', 'id']} />

            <FloatingFormItem
              name={['supplier', 'name']}
              label={intl.formatMessage({
                id: 'purchases.releases.form.supplier',
              })}
              rules={[defaultEmptyFieldError]}
            >
              <InputSearch
                inputProps={{ disabled: true }}
                buttonProps={{
                  onClick: modalSupplierAction.open,
                  disabled:
                    purchase?.situation === Status.canceled ||
                    purchase?.hasPurchaseReturned ||
                    isProPartsPurchase,
                }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={1} lg={2} xl={1}>
            <Button
              onClick={() => setSuppliersCreateVisible.toggle()}
              style={{ marginTop: 6 }}
              size="large"
              icon={<UserPlus width={20} />}
              title={intl.formatMessage({
                id: 'purchases.releases.form.add.supplier',
              })}
              disabled={
                purchase?.situation === Status.canceled ||
                purchase?.hasPurchaseReturned ||
                isProPartsPurchase
              }
            />
          </Col>

          <Col span={24} md={6} lg={4}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchases.releases.form.releaseDate',
              })}
              name="releaseDate"
              initialValue={moment()}
            >
              <InputDate disabled={purchase?.situation === Status.canceled} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={6} lg={4}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchases.releases.form.issuanceDate',
              })}
              name="issueDate"
              rules={[defaultEmptyFieldError]}
            >
              <InputDate
                disabled={
                  purchase?.situation === Status.canceled ||
                  disableIssueDateForBosch ||
                  isProPartsPurchase
                }
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={6} lg={4}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchases.releases.form.departureDate',
              })}
              name="departureDate"
            >
              <InputDate disabled={purchase?.situation === Status.canceled} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={6} lg={6} xl={4}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchases.releases.form.noteTemplate',
              })}
              name="modelNf"
            >
              <InputText
                onlyNumber
                disabled={purchase?.situation === Status.canceled}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={3} lg={4} xl={4}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchases.releases.form.series',
              })}
              name="seriesNf"
            >
              <InputText
                onlyNumber
                disabled={purchase?.situation === Status.canceled}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={9} lg={8} xl={4}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchases.releases.form.operationDestination',
              })}
              name="operationDestination"
              rules={[defaultEmptyFieldError]}
              initialValue={PurchaseOperationDestination.internalOperation}
            >
              <Select
                style={{ width: '100%' }}
                options={[
                  {
                    value: PurchaseOperationDestination.internalOperation,
                    label: intl.formatMessage({
                      id: 'purchases.releases.form.internalOperation',
                    }),
                  },
                  {
                    value: PurchaseOperationDestination.interstateOperation,
                    label: intl.formatMessage({
                      id: 'purchases.releases.form.interstateOperation',
                    }),
                  },
                  {
                    value: PurchaseOperationDestination.foreignOperation,
                    label: intl.formatMessage({
                      id: 'purchases.releases.form.foreignOperation',
                    }),
                  },
                ]}
                disabled={purchase?.situation === Status.canceled}
              />
            </FloatingFormItem>
          </Col>

          <Col md={8} span={24} lg={6} xl={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchases.releases.form.documentNumber',
              })}
              name="documentNumber"
            >
              <InputText
                onChange={({ target: { value } }) =>
                  form.setFieldValue('documentNumber', value.trim())
                }
                onlyNumber
                maxLength={9}
                disabled={purchase?.situation === Status.canceled}
              />
            </FloatingFormItem>
          </Col>

          <Col span={14} md={7} lg={6} xl={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchases.releases.form.shippingType',
              })}
              name="deliveryType"
              initialValue={PurchaseDeliveryType.recipient}
            >
              <Select
                style={{ width: '100%' }}
                options={[
                  {
                    value: PurchaseDeliveryType.recipient,
                    label: intl.formatMessage({
                      id: 'purchases.releases.form.recipient',
                    }),
                  },
                  {
                    value: PurchaseDeliveryType.issuer,
                    label: intl.formatMessage({
                      id: 'purchases.releases.form.issuer',
                    }),
                  },
                  {
                    value: PurchaseDeliveryType.freeShipping,
                    label: intl.formatMessage({
                      id: 'purchases.releases.form.freeShipping',
                    }),
                  },
                  {
                    value: PurchaseDeliveryType.the3rd,
                    label: intl.formatMessage({
                      id: 'purchases.releases.form.the3rd',
                    }),
                  },
                ]}
                disabled={purchase?.situation === Status.canceled}
              />
            </FloatingFormItem>
          </Col>

          <Col span={10} md={5} lg={4} xl={2}>
            <Form.Item name="freightInvoice" noStyle valuePropName="checked">
              <Checkbox
                style={{ marginTop: '12px' }}
                disabled={
                  purchase?.situation === Status.canceled ||
                  purchase?.freightInvoice ||
                  !!purchaseItems.length
                }
              >
                {intl.formatMessage({
                  id: 'purchases.releases.form.invoiceFreight',
                })}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={24} lg={14} xl={8}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchases.releases.form.documentType',
              })}
              name="documentTypeId"
              rules={[defaultEmptyFieldError]}
            >
              <DocumentTypesInfiniteSelectField
                disabled={
                  purchase?.situation === Status.canceled || isProPartsPurchase
                }
                hasFinancial={purchase?.hasFinancial}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={24} lg={24} xl={12}>
            <Row gutter={12}>
              <Col span={24} md={6} lg={6} xl={6}>
                <FloatingFormItem
                  label={intl.formatMessage({
                    id: 'purchases.releases.form.shippingValue',
                  })}
                  name="deliveryValue"
                  initialValue={0}
                >
                  <InputDecimal
                    min={0}
                    disabled={purchase?.situation === Status.canceled}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={6} lg={6} xl={6}>
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const isIndividuallyExpenses = getFieldValue(
                      'individuallyExpenses',
                    );

                    return (
                      <>
                        <FloatingFormItem
                          label={intl.formatMessage({
                            id: 'purchases.releases.form.expenditure',
                          })}
                          name="expensesValue"
                          initialValue={0}
                        >
                          <InputDecimal
                            disabled={
                              isIndividuallyExpenses ||
                              purchase?.situation === Status.canceled
                            }
                            min={0}
                          />
                        </FloatingFormItem>
                      </>
                    );
                  }}
                </Form.Item>
              </Col>

              <Col span={24} md={6} lg={6} xl={6}>
                <FloatingFormItem
                  label={intl.formatMessage({
                    id: 'purchases.releases.form.discountValue',
                  })}
                  name="discountValue"
                  initialValue={0}
                >
                  <InputDecimal disabled />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={6} lg={6} xl={6}>
                <FloatingFormItem
                  label={intl.formatMessage({
                    id: 'purchases.releases.form.amount',
                  })}
                  name="totalAmount"
                  initialValue={0}
                >
                  <InputDecimal disabled />
                </FloatingFormItem>
              </Col>
            </Row>
          </Col>

          <Col span={24} md={12} lg={12} xl={6}>
            <Form.Item
              name="applyDeliveryExpensesBcIcmsSt"
              valuePropName="checked"
              noStyle
            >
              <Checkbox disabled={purchase?.situation === Status.canceled}>
                {intl.formatMessage({
                  id: 'purchases.releases.form.includeIcms',
                })}
              </Checkbox>
            </Form.Item>
            <Row>
              <Form.Item
                name="applyDeliveryExpensesBcIpi"
                valuePropName="checked"
                noStyle
              >
                <Checkbox disabled={purchase?.situation === Status.canceled}>
                  {intl.formatMessage({
                    id: 'purchases.releases.form.includeIpi',
                  })}
                </Checkbox>
              </Form.Item>
            </Row>
          </Col>

          <Col span={24} md={12} lg={12} xl={6}>
            <Form.Item
              name="deductIcmsUnencumbered"
              valuePropName="checked"
              noStyle
            >
              <Checkbox disabled={purchase?.situation === Status.canceled}>
                {intl.formatMessage({
                  id: 'purchases.releases.form.icmsDishonored',
                })}
              </Checkbox>
            </Form.Item>

            <Group gap={12}>
              <Form.Item
                name="individuallyExpenses"
                valuePropName="checked"
                noStyle
              >
                <Checkbox disabled={purchase?.situation === Status.canceled}>
                  {intl.formatMessage({
                    id: 'purchases.releases.form.expensesIndividually',
                  })}
                </Checkbox>
              </Form.Item>

              <Dropdown
                overlay={expensesIndividuallyMenu}
                trigger={['click']}
                arrow
                placement="topCenter"
              >
                <Info size={20} cursor={'pointer'} />
              </Dropdown>
            </Group>
          </Col>

          <Col xs={24} span={24} md={24}>
            <FloatingFormItem
              name="additionalData"
              label={
                <FormattedMessage id="purchases.releases.form.additionalData" />
              }
            >
              <Input.TextArea rows={3} style={{ height: '102px' }} />
            </FloatingFormItem>
          </Col>
        </Row>

        <Card style={{ marginBottom: 24 }} title="Dados complementares">
          <Row gutter={[16, 16]}>
            <Col span={24} md={12} lg={7}>
              <FloatingFormItem
                name="deliveryPrevisionDate"
                label="Previsão de entrega"
              >
                <InputDate />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name="governmentType"
                label="Tipo Governamental"
              >
                <SelectGovernmentType allowClear />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={9}>
              <FloatingFormItem
                name="governmentOperation"
                label="Operação Governamental"
              >
                <SelectGovernmentOperation allowClear />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>
      </Form>

      <SuppliersCreateModal
        form={form}
        visible={suppliersCreateVisible}
        handleVisible={setSuppliersCreateVisible.toggle}
      />

      <SuppliersSelectedModal
        visible={modalSupplierVisible}
        handleVisible={modalSupplierAction.toggle}
        noSelection
        onSupplierSelect={handleSupplierSelect}
      />
    </>
  );
}

export default PurchasesReleaseForm;
